/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import {
    AclModel,
    FipModel,
    L7BackendServiceModel,
    L7RealServiceModel,
    L7VirtualServiceModel,
    TemplateModel,
    TemplateOptions
} from '../../../models';
import {
    AclsComponent,
    Layer4BackendComponent,
    Layer4FrontendComponent,
    Layer4RealComponent,
    Layer7BackendComponent,
    Layer7FrontendComponent,
    ServicesMonitorComponent
} from '../../../components';
import { ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { FIPApiService } from '../../../http';
import { Layer7RealComponent } from '../../../components/serviceComponents/l7Services/servicesReal/layer7-real.component';
import { LogService, NotifyService, UtilsService } from '../../../../../../core/services';
import { MultipleFloatingIpsModalComponent } from '../../../modals';
import { Observable } from 'rxjs';
import { WizardService } from './wizard.service';
import { WizardTemplateService } from './wizardTemplate.service';
import { WizardTitleEnum } from '../../../enums';

@Injectable()
export class Layer47WizardService {
    /**
     * Store the frontend to edit.
     * @private
     */
    private frontend: L7VirtualServiceModel;

    /**
     * Store the frontends.
     * @private
     */
    private frontends: L7VirtualServiceModel[] = [];

    /**
     * Store the backends.
     * @private
     */
    private backends: L7BackendServiceModel[] = [];

    /**
     * Store the backends.
     * @private
     */
    private realServers: L7RealServiceModel[] = [];

    /**
     * Store the acl rules.
     * @private
     */
    private aclRules: AclModel[] = [];

    /**
     * Store list of floating IPs.
     */
    public fips: FipModel[] = [];

    constructor(
        private fipApiService: FIPApiService,
        private logService: LogService,
        private utilsService: UtilsService,
        private wizardService: WizardService,
        private notifyService: NotifyService,
        private wizardTemplateService: WizardTemplateService
    ) {
    }

    /**
     * Set services.
     * @param services
     */
    public setServices(services: any): void {
        const { frontends, backends, realServers, acls, fips, frontend } = services;
        this.frontend = frontend || {};
        this.frontends = frontends || [];
        this.backends = backends || [];
        this.realServers = realServers || [];
        this.aclRules = acls || [];
        this.fips = fips || [];
    }

    /**
     * Get services.
     */
    public getServices(): any {
        return {
            frontend: this.frontend,
            frontends: this.frontends,
            backends: this.backends,
            realServers: this.realServers,
            aclRules: this.aclRules,
            fips: this.fips
        };
    }

    /**
     * Get a list of service ids and names.
     */
    public getListOfServiceIdsAndNames(): any[] {
        const output: any[] = [];
        const services: any = this.getServices();
        delete services.frontend;

        for (const serviceName in services) {
            if (services.hasOwnProperty(serviceName)) {
                const service: any = services[serviceName];
                for (const serviceItem of service) {
                    output.push({
                        name: serviceItem.name,
                        id: serviceItem.id
                    });
                }
            }
        }

        return output;
    }

    /**
     * Get item Ids and name
     */
    public getItemIdAndName(): any {
        const frontends: any[] = this.getIdAndName(this.frontends);
        const backends: any[] = this.getIdAndName(this.backends);

        return frontends.concat(backends);
    }

    /**
     * Get items and names.
     * @param services
     * @private
     */
    private getIdAndName(services: any[]): any[] {
        return services.map((item: any): any => {
            return { id: item.id, name: item.name };
        });
    }

    /**
     * Get a particular service by ID.
     * @param serviceId
     * @param services
     * @private
     */
    private getServiceById(serviceId: string, services: L7VirtualServiceModel[]): L7VirtualServiceModel {
        for (const service of services) {
            if (service.id === serviceId) {
                return service;
            }
        }
    }

    /**
     * Set monitor template state.
     * @param template
     * @param backends
     * @param hangingBackends
     */
    public setMonitorTemplateState(template: TemplateModel, backends: L7BackendServiceModel[], hangingBackends: any[]): TemplateModel {
        if (!backends.filter(item => item.mode === 'http').length
            || !!hangingBackends.find(item => item.mode === 'http')) {
            template.disabled = true;
            template.disabledMessage = 'There are no http backends.<br>You need to create one before this option is enabled';
        } else {
            // Its possible there was a previous load and these were set, so they should be deleted.
            delete template.disabled;
            delete template.disabledMessage;
        }

        return template;
    }

    /**
     * Check for missing IPs.
     */
    public checkForMissingIps(modalContainer: ViewContainerRef, componentReference: ComponentRef<any>): void {
        const sections = this.utilsService.getObjectValue(this.wizardService.selectedTemplate, 'sections');
        const sectionNames: string[] = Object.keys(sections);
        const sectionsWithFips: WizardTitleEnum[] = [WizardTitleEnum.FRONTENDS, WizardTitleEnum.MONITORS];
        const matchingSections = sectionNames.filter((item: WizardTitleEnum) => sectionsWithFips.includes(item));
        if (matchingSections.length) {
            if (matchingSections.length > 1) {
                this.logService.error('Layer47WizardService:checkForMissingIps', 'More than one matching section found', matchingSections);
            } else {
                this.setupFloatingIps(modalContainer, componentReference, matchingSections[0]);
            }
        }
    }

    /**
     * Sets up floating ips in the wizard.
     * @private
     */
    private setupFloatingIps(modalContainer: ViewContainerRef, componentReference: ComponentRef<any>, section: string): void {
        const frontends = this.utilsService.getObjectValue(
            this.wizardService.selectedTemplate, 'sections.' + [section]
        );

        const fipReference = this.utilsService.getObjectValue(this.wizardService.selectedTemplate, 'fipsReference');
        const isUserTemplate = this.utilsService.getObjectValue(this.wizardService.selectedTemplate, 'isUserTemplate');
        if (frontends?.length === fipReference?.length) {
            const missingIpReferences: any[] = this.getMissingReferences(fipReference);
            if (missingIpReferences.some((item: { id: any; }): boolean => item.id === undefined)) {
                this.setupMissingIpModal(modalContainer, componentReference, missingIpReferences);
            } else {
                this.updateFrontendsFipData(missingIpReferences);
            }
        } else if (isUserTemplate) {
            const message: string = 'This template is an older template that doesn\'t have a fip reference in it,' +
                'you will need to manually check that the Floating IP address exists';
            this.notifyService.warning(message, { static: true });
            this.logService.warn('Layer47WizardService: setupFloatingIps', message);
        } else {
            this.logService.debug('Layer47WizardService: setupFloatingIps', 'non user template loaded');
        }
    }

    /**
     * Get missing references.
     * @param fipReference
     * @private
     */
    private getMissingReferences(fipReference: any): any[] {
        const missingReference: any = [];
        for (const ip of fipReference) {
            const match = this.fips.find((item: any): boolean => item.ip === ip);
            missingReference.push({ id: match?.id ? match.id : undefined, ip: match?.ip ? match?.ip : ip });
        }

        return missingReference;
    }

    /**
     * Set up the missing IP modal so the user can assign the missing IPs to groups and then create them.
     * @param modalContainer
     * @param componentReference
     * @param missingIpReferences
     * @private
     */
    private setupMissingIpModal(
        modalContainer: ViewContainerRef,
        componentReference: ComponentRef<any>,
        missingIpReferences: string[]
    ): void {
        const addFIPsModal: ComponentRef<any> = modalContainer.createComponent(MultipleFloatingIpsModalComponent);

        // Send missing IPs to missing modal.
        addFIPsModal.instance.missingIpReferences = missingIpReferences;
        addFIPsModal.instance.callBack = this.missingIpCallBackHelper.bind(this);

        // Assign component reference
        componentReference = addFIPsModal;
    }

    /**
     * Helper to reference the get floating ips subscriber.
     * @param selectedFipIds
     * @private
     */
    private missingIpCallBackHelper(selectedFipIds: string[]): void {
        this.getFloatingIps()
            .subscribe({
                next: (data: FipModel[]): void => {
                    this.fips = data;
                    this.updateFrontendsFipData(selectedFipIds);
                }
            });
    }

    /**
     * Get floating ips.
     * @private
     */
    private getFloatingIps(): Observable<FipModel[]> {
        return new Observable(
            (observer) => {
                this.fipApiService.getFloatingIPs()
                    .subscribe({
                        next: (response: FipModel[]): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            });
    }

    /**
     * If the floating IP is not missing, match the IP with its ID and set it as the value in the frontend.
     * @param selectedFipIds
     * @private
     */
    private updateFrontendsFipData(selectedFipIds: any[]): void {
        const service = this.wizardService.selectedTemplate.type === 'monitor'
            ? WizardTitleEnum.MONITORS : WizardTitleEnum.FRONTENDS;
        const servicesReference = this.utilsService.getObjectValue(this.wizardService.controllerState,
            `${service}`);

        if (servicesReference && selectedFipIds.length) {
            for (let i = 0; i < servicesReference.length; i++) {
                const ipMatch = selectedFipIds[i];
                if (ipMatch) {
                    servicesReference[i].fipId.value = ipMatch?.id;
                } else {
                    this.logService.error('Layer47WizardService: updateFrontendsFipData',
                        'Unable to match a FIP for the selected template');
                }
            }
        } else {
            const message = 'This template has a mismatch between the number of frontends and floating IPs references';
            this.notifyService.warning(message, {
                static: true
            });
            this.logService.warn('Layer47WizardService: matchIpsToService', message);
        }
    }

    /**
     * Get the layer 4 components to be loaded.
     * @param serviceName
     */
    public getLayer4Components(serviceName: string): any {
        switch (serviceName) {
            case WizardTitleEnum.FRONTENDS:
                return Layer4FrontendComponent;
            case WizardTitleEnum.BACKENDS:
                return Layer4BackendComponent;
            case WizardTitleEnum.REALSERVERS:
                return Layer4RealComponent;
            case WizardTitleEnum.ACLS:
                return AclsComponent;
        }
    }

    /**
     * Get the layer 7 components to be loaded.
     * @param serviceName
     */
    public getLayer7Components(serviceName: string): any {
        switch (serviceName) {
            case WizardTitleEnum.FRONTENDS:
                return Layer7FrontendComponent;
            case WizardTitleEnum.BACKENDS:
                return Layer7BackendComponent;
            case WizardTitleEnum.REALSERVERS:
                return Layer7RealComponent;
            case WizardTitleEnum.MONITORS:
                return ServicesMonitorComponent;
            case WizardTitleEnum.ACLS:
                return AclsComponent;
        }
    }

    /**
     * Set up frontend template.
     * @param frontendId
     * @param availableTemplates
     * @param templateType
     * @param duplicate
     */
    public setUpFrontendTemplate(
        frontendId: string,
        availableTemplates: TemplateModel[],
        templateType: string,
        duplicate?: boolean
    ): any {
        const serviceTemplate: any = this.wizardTemplateService.getClonedTemplate(availableTemplates, templateType);
        const service: L7VirtualServiceModel = this.getServiceById(frontendId, this.frontends);
        const serviceType: string = service.type === 'monitor' ? WizardTitleEnum.MONITORS : WizardTitleEnum.FRONTENDS;
        const frontendObject: any = frontendId
            ? this.frontends.find((item: L7VirtualServiceModel): boolean => item.id === frontendId)
            : { id: 'temp-' + Date.now() + Math.random() };

        const frontendOptions: any = {
            inputsToShow: ['name', 'ports', 'fipId', 'monitorUri'],
            inputsToHide: [],
            inputsToDelete: ['statsId', 'aclIds']
        };

        if (duplicate) {
            const duplicateFrontend: L7VirtualServiceModel = this.utilsService.duplicateObject(frontendObject);
            duplicateFrontend['id'] = 'temp-' + Date.now() + Math.random();

            this.wizardService.controllerState = {
                [serviceType]: [
                    this.wizardTemplateService.createServiceFromTemplate(
                        serviceTemplate, serviceType, duplicateFrontend, frontendOptions
                    )
                ]
            };

            if (serviceType === WizardTitleEnum.FRONTENDS) {
                this.utilsService.createArrayIfNotExist(this.wizardService.controllerState, WizardTitleEnum.ACLS);
                const aclIds: string[] = duplicateFrontend?.aclIds;
                if (aclIds?.length) {
                    const newAcls: AclModel[] = this.utilsService.duplicateArray(this.aclRules);
                    const acls: AclModel[] = newAcls.filter((item: any): boolean => aclIds.includes(item.id));
                    for (const acl of acls) {
                        acl.id = 'temp-' + Date.now() + Math.random();
                        const convertedAcl: any = this.wizardTemplateService.convertServiceToTemplate(
                            serviceTemplate, WizardTitleEnum.ACLS, acl
                        );
                        this.cleanConvertedAcl(convertedAcl);
                        this.wizardService.controllerState[WizardTitleEnum.ACLS].push(convertedAcl);
                    }
                }
            }
        } else {
            this.wizardService.controllerState = {
                [serviceType]: [
                    this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, serviceType, frontendObject)
                ]
            };
        }

        if (frontendId || duplicate) {
            this.deleteSections(serviceTemplate, [WizardTitleEnum.BACKENDS, WizardTitleEnum.REALSERVERS, WizardTitleEnum.ACLS]);
        }

        return [serviceTemplate];
    }

    /**
     * Delete sections from template.
     * @param template
     * @param sections
     * @private
     */
    private deleteSections(template: any, sections: string[]): void {
        for (const section of sections) {
            delete template.sections[section];
        }
    }

    /**
     * Clean acl of $1 value.
     * @param acl
     * @private
     */
    private cleanConvertedAcl(acl: any): void {
        if (acl.redirectLocation?.value.includes('$')) {
            acl.redirectLocation.value = '';
        }
    }

    /**
     * Setup backend template for wizard.
     * @param backendId
     * @param availableTemplates
     * @param templateType
     * @param duplicate
     */
    public setUpBackendTemplate(
        backendId: string,
        availableTemplates: TemplateModel[],
        templateType: string,
        duplicate?: boolean
    ): any {
        const serviceTemplate: any = this.wizardTemplateService.getClonedTemplate(availableTemplates, templateType);
        const backendObject: any = backendId
            ? this.backends.find(backendServer => backendServer.id === backendId)
            : { id: 'temp-' + Date.now() + Math.random() };
        const aclObject = {
            condition: null,
            id: 'temp-' + Date.now() + Math.random(),
            conditionType: null,
            redirectLocation: null,
            serviceId: this.frontend.id
        };

        const frontendOptions = { inputsToShow: [], inputsToHide: ['all'], };
        const aclOptions = { inputsToShow: ['conditionType', 'redirectLocation', 'redirectType', 'serviceId'] };

        // Add a backend
        if (!backendId) {
            /* eslint-disable max-len */
            this.wizardService.controllerState = {
                [WizardTitleEnum.FRONTENDS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.FRONTENDS, this.frontend, frontendOptions)],
                [WizardTitleEnum.BACKENDS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.BACKENDS, backendObject)],
                [WizardTitleEnum.ACLS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.ACLS, aclObject, aclOptions)]
            };
            /* eslint-enable max-len */
            serviceTemplate.sections[WizardTitleEnum.ACLS][0].redirectLocation.hidden = false;
        } else {
            // Duplicate a backend
            if (duplicate) {
                const backendOptions = {
                    inputsToShow: ['name'],
                    inputsToDelete: ['statsId']
                };

                backendObject.id = 'temp-' + Date.now() + Math.random();
                /* eslint-disable max-len */
                this.wizardService.controllerState = {
                    [WizardTitleEnum.FRONTENDS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.FRONTENDS, this.frontend, frontendOptions)],
                    [WizardTitleEnum.BACKENDS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.BACKENDS, backendObject, backendOptions)],
                    [WizardTitleEnum.ACLS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.ACLS, aclObject, aclOptions)]
                };
                /* eslint-enable max-len */
                serviceTemplate.sections[WizardTitleEnum.ACLS][0].redirectLocation.hidden = false;
            } else {
                // Edit a backend
                // Populated backendObject
                this.wizardService.controllerState = {
                    // eslint-disable-next-line max-len
                    [WizardTitleEnum.BACKENDS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.BACKENDS, backendObject)]
                };

                this.deleteSections(serviceTemplate, [WizardTitleEnum.ACLS]);
            }
        }

        // Remove unneeded steps from template.
        this.deleteSections(serviceTemplate, [WizardTitleEnum.FRONTENDS, WizardTitleEnum.REALSERVERS]);

        return [serviceTemplate];
    }

    /**
     * Set up the template for Real servers.
     * @param realServerId
     * @param backendId
     * @param availableTemplates
     * @param duplicate
     */
    public setUpRealServerTemplate(
        realServerId: any,
        backendId: string,
        availableTemplates: TemplateModel[],
        duplicate?: boolean
    ): any {
        const serviceTemplate: any = this.wizardTemplateService.getClonedTemplate(availableTemplates);
        const backend = this.backends.find(item => item.id === backendId);
        const realServerObject: any = realServerId
            ? this.realServers.find((realServer: { id: any; }): boolean => realServer.id === realServerId)
            : { id: 'temp-' + Date.now() + Math.random() };

        const backendOptions = { inputsToShow: [], inputsToHide: ['all'], };

        // Add real server
        if (!realServerId && !duplicate) {
            /* eslint-disable max-len */
            this.wizardService.controllerState = {
                [WizardTitleEnum.BACKENDS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.BACKENDS, backend, backendOptions)],
                [WizardTitleEnum.REALSERVERS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.REALSERVERS, realServerObject)]
            };
            /* eslint-enable max-len */
        } else {
            // Edit duplicate real server
            const realServerOptions: TemplateOptions = {};

            if (duplicate) {
                this.wizardService.excludeFromAddAnother = WizardTitleEnum.REALSERVERS;
                realServerOptions.inputsToShow = ['name', 'port', 'ip'];
                realServerOptions.inputsToHide = ['all'];
                realServerOptions.inputsToDelete = ['statsId'];
                realServerObject.id = 'temp-' + Date.now() + Math.random();
            }
            /* eslint-disable max-len */
            this.wizardService.controllerState = {
                [WizardTitleEnum.BACKENDS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.BACKENDS, backend, backendOptions)],
                [WizardTitleEnum.REALSERVERS]: [this.wizardTemplateService.createServiceFromTemplate(serviceTemplate, WizardTitleEnum.REALSERVERS, realServerObject, realServerOptions)]
            };
            /* eslint-enable max-len */
        }

        // Remove unneeded steps from template.
        this.deleteSections(serviceTemplate, [WizardTitleEnum.FRONTENDS, WizardTitleEnum.BACKENDS, WizardTitleEnum.ACLS]);

        serviceTemplate.sections[WizardTitleEnum.REALSERVERS] = serviceTemplate.hiddenSections[WizardTitleEnum.REALSERVERS];

        return [serviceTemplate];
    }

    /**
     * Handle Ids.
     * @param serviceType
     * @param property
     * @param tempIds
     * @param serviceIndex
     */
    public handleIds(serviceType: string, property: string, tempIds: any, serviceIndex: number): any {
        const templatePatch: object = {};
        if (property === 'aclIds' && serviceType === WizardTitleEnum.FRONTENDS) {
            templatePatch['value'] = [tempIds[WizardTitleEnum.ACLS][serviceIndex]];
        }

        if (property === 'backendID' && serviceType === WizardTitleEnum.REALSERVERS) {
            templatePatch['value'] = tempIds[WizardTitleEnum.BACKENDS][serviceIndex];
        }

        if (property === 'serviceId' && serviceType === WizardTitleEnum.ACLS) {
            templatePatch['value'] = tempIds[WizardTitleEnum.FRONTENDS][serviceIndex];
        }

        if (property === 'redirectLocation' && serviceType === WizardTitleEnum.ACLS) {
            templatePatch['value'] = tempIds[WizardTitleEnum.BACKENDS][serviceIndex];
        }

        if (property === 'id') {
            templatePatch['value'] = tempIds[serviceType][serviceIndex];
        }

        templatePatch['hidden'] = true;

        return templatePatch;
    }
}
